import { ISingleService } from "types";
import { uniqueOptionIds, withOption, withoutOption, withReplacedCategory } from "lib/optionIds";
import { RESERVATION_OPTIONS } from "config/consts";

export type ReservationPayloadType = {
  serviceId: string;
  startTime: string;
  optionIds: string[];
  branchIds: string[];
  scheduleId: string;
  birthYear?: string;
  visitTypeId: string;
  therapist: string;
  selectedUserId?: string;
  selectedCity?: string;
  isOnline: boolean;
};

export type ReservationPayloadAction =
  | {
      type: "setService";
      serviceId: string;
      prevActiveService: ISingleService | null;
      newActiveService: ISingleService;
      locale: string;
    }
  | { type: "setVisitType"; optionId: string; activeService: ISingleService | null }
  | { type: "setBranchOnline"; onlineOptionId: string | undefined }
  | { type: "setBranchOffline"; onlineOptionId: string | undefined; branchIds: string[] }
  | { type: "setLanguageOption"; optionId: string | undefined; enabled: boolean }
  | { type: "selectLanguageOption"; optionId: string | undefined; activeService: ISingleService }
  | { type: "setTherapist"; therapist: string }
  | { type: "setBirthYear"; birthYear: string | null }
  | { type: "setIsOnline"; isOnline: boolean; onlineOptionId: string | undefined };

const findOptionTranslation = (
  service: ISingleService | null | undefined,
  optionId: string,
  locale: string,
): string | undefined => service?.options.find((el) => el.id === optionId)?.translations[locale];

export const reservationPayloadReducer = (
  state: ReservationPayloadType,
  action: ReservationPayloadAction,
): ReservationPayloadType => {
  switch (action.type) {
    case "setService": {
      const { serviceId, prevActiveService, newActiveService, locale } = action;
      const newOptionIds: string[] = [];
      let newVisitType: string | null = null;

      if (state.optionIds && prevActiveService) {
        state.optionIds.forEach((optionId) => {
          const translation = findOptionTranslation(prevActiveService, optionId, locale);
          if (translation) {
            const newOption = newActiveService.options.find((el) => el.translations[locale] === translation);
            if (newOption) {
              newOptionIds.push(newOption.id);
              if (newOption.category === RESERVATION_OPTIONS.VISIT_TYPE) {
                newVisitType = newOption.id;
              }
            }
          }
        });
      }

      if (state.isOnline) {
        const onlineId = newActiveService.options.find(
          (el) => el.category === RESERVATION_OPTIONS.LOCATION && el.publicVisible !== false,
        )?.id;

        if (!onlineId) {
          return {
            ...state,
            serviceId,
            optionIds: uniqueOptionIds(newOptionIds),
            visitTypeId: newVisitType ?? "",
            therapist: state.selectedUserId ? state.therapist : "",
          };
        }

        const deduped = withOption(uniqueOptionIds(newOptionIds), onlineId);
        return {
          ...state,
          serviceId,
          optionIds: deduped,
          visitTypeId: newVisitType ?? "",
          therapist: state.selectedUserId ? state.therapist : "",
        };
      }

      return {
        ...state,
        serviceId,
        optionIds: uniqueOptionIds(newOptionIds),
        visitTypeId: newVisitType ?? "",
        therapist: state.selectedUserId ? state.therapist : "",
      };
    }

    case "setVisitType": {
      const { optionId, activeService } = action;
      const optionIds = uniqueOptionIds(
        withReplacedCategory(state.optionIds, activeService, RESERVATION_OPTIONS.VISIT_TYPE, optionId),
      );
      return {
        ...state,
        visitTypeId: optionId,
        optionIds,
        therapist: state.selectedUserId ? state.therapist : "",
      };
    }

    case "setBranchOnline":
      return {
        ...state,
        isOnline: true,
        branchIds: [],
        optionIds: uniqueOptionIds(withOption(state.optionIds, action.onlineOptionId)),
        therapist: state.selectedUserId ? state.therapist : "",
      };

    case "setBranchOffline":
      return {
        ...state,
        isOnline: false,
        branchIds: action.branchIds,
        optionIds: uniqueOptionIds(withoutOption(state.optionIds, action.onlineOptionId)),
        therapist: state.selectedUserId ? state.therapist : "",
      };

    case "setLanguageOption": {
      const { optionId, enabled } = action;
      return {
        ...state,
        optionIds: uniqueOptionIds(
          enabled ? withOption(state.optionIds, optionId) : withoutOption(state.optionIds, optionId),
        ),
      };
    }

    case "selectLanguageOption":
      return {
        ...state,
        optionIds: uniqueOptionIds(
          withReplacedCategory(state.optionIds, action.activeService, RESERVATION_OPTIONS.LANGUGAGE, action.optionId),
        ),
      };

    case "setTherapist":
      return { ...state, therapist: action.therapist };

    case "setBirthYear":
      return { ...state, birthYear: action.birthYear ?? undefined };

    case "setIsOnline":
      return {
        ...state,
        isOnline: action.isOnline,
        optionIds: uniqueOptionIds(
          action.isOnline
            ? withOption(state.optionIds, action.onlineOptionId)
            : withoutOption(state.optionIds, action.onlineOptionId),
        ),
      };

    default:
      return state;
  }
};
